import { createTheme, ThemeOptions } from '@mui/material/styles';
import { colors } from './colors';

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
    success: colors.success,
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: colors.text,
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: colors.primary.main,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      color: colors.primary.main,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      color: colors.text.secondary,
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
      color: colors.text.primary,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: colors.text.secondary,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 16, // Tarjetas más redondeadas estilo PayPal
  },
  shadows: [
    'none',
    '0px 2px 8px rgba(10, 37, 64, 0.02)',
    '0px 4px 16px rgba(10, 37, 64, 0.03)',
    '0px 8px 24px rgba(10, 37, 64, 0.04)',
    '0px 12px 32px rgba(10, 37, 64, 0.06)',
    '0px 16px 40px rgba(10, 37, 64, 0.08)',
    '0px 20px 48px rgba(10, 37, 64, 0.1)',
    '0px 24px 56px rgba(10, 37, 64, 0.12)',
    '0px 2px 8px rgba(10, 37, 64, 0.02)',
    '0px 4px 16px rgba(10, 37, 64, 0.03)',
    '0px 8px 24px rgba(10, 37, 64, 0.04)',
    '0px 12px 32px rgba(10, 37, 64, 0.06)',
    '0px 16px 40px rgba(10, 37, 64, 0.08)',
    '0px 20px 48px rgba(10, 37, 64, 0.1)',
    '0px 24px 56px rgba(10, 37, 64, 0.12)',
    '0px 2px 8px rgba(10, 37, 64, 0.02)',
    '0px 4px 16px rgba(10, 37, 64, 0.03)',
    '0px 8px 24px rgba(10, 37, 64, 0.04)',
    '0px 12px 32px rgba(10, 37, 64, 0.06)',
    '0px 16px 40px rgba(10, 37, 64, 0.08)',
    '0px 20px 48px rgba(10, 37, 64, 0.1)',
    '0px 24px 56px rgba(10, 37, 64, 0.12)',
    '0px 2px 8px rgba(10, 37, 64, 0.02)',
    '0px 4px 16px rgba(10, 37, 64, 0.03)',
    '0px 8px 24px rgba(10, 37, 64, 0.04)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30, // Botones ovalados estilo PayPal (pill-shaped)
          padding: '10px 28px',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 112, 224, 0.2)',
          },
        },
        containedPrimary: {
          backgroundColor: colors.secondary.main, // Usar Celeste PayPal como principal para CTA
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: colors.secondary.dark,
          },
        },
        outlinedPrimary: {
          borderColor: colors.secondary.main,
          color: colors.secondary.main,
          '&:hover': {
            borderColor: colors.secondary.dark,
            backgroundColor: 'rgba(0, 112, 224, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid #E2E8F0', // Borde gris suave en vez de sombras toscas
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.01)',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 8px 24px rgba(10, 37, 64, 0.04)',
            borderColor: '#CBD5E1',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.01)',
          border: '1px solid #E2E8F0',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: colors.primary.main,
          boxShadow: 'none',
          borderBottom: '1px solid #E2E8F0',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #E2E8F0',
          boxShadow: 'none',
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8, // Inputs ligeramente redondeados muy limpios
            transition: 'all 0.2s',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#94A3B8',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1.5px',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#CBD5E1',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);
export default theme;
